import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { PcbTestEntity, PcbTestState, StatusFilter } from './types';
import {
  LoadDataAction,
  EntityState,
  SortPayloadAction,
  DataPayload,
} from '../types';
import { structuredFetch } from '../../api/api';
import { AxiosInstance } from 'axios';
import { Auth } from '../auth/types';

const initialState: PcbTestState = {
  data: [],
  currentPage: 0,
  resultsPerPage: 8,
  totalPages: 0,
  sortColumn: 'createdAt',
  sortDirection: -1,
  selectedFilters: { status: 'ALL' },
};

const pcbTestSlice = createSlice({
  name: 'pcbTests',
  initialState,
  reducers: {
    setPcbTests: (
      state,
      { payload }: PayloadAction<DataPayload<PcbTestEntity>>
    ) => {
      state.data = payload.entities;
      state.totalPages = payload.totalPages;
    },
    sortPcbTests: (state, { payload }: SortPayloadAction<PcbTestEntity>) => {
      state.sortColumn = payload.column;
      state.sortDirection = payload.direction;
    },
    setSearchFilter: (state, { payload }: PayloadAction<string>) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        search: payload === '' ? undefined : payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setDeviceTypesFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        deviceTypes: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setDateFilter: (
      state,
      { payload }: PayloadAction<[string | undefined, string | undefined]>
    ) => {
      const [startDate, endDate] = payload;
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        startDate,
        endDate,
      });
      state.currentPage = initialState.currentPage;
    },
    setStatusFilter: (state, { payload }: PayloadAction<StatusFilter>) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        status: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    resetPage: (state) => {
      state.selectedFilters = initialState.selectedFilters;
      state.sortColumn = initialState.sortColumn;
      state.sortDirection = initialState.sortDirection;
      state.currentPage = initialState.currentPage;
    },
  },
});

export const loadPcbTests: LoadDataAction<PcbTestEntity> = async (
  axios: AxiosInstance,
  auth: Auth,
  state: EntityState<PcbTestEntity>
) => {
  const {
    resultsPerPage,
    currentPage,
    sortColumn,
    sortDirection,
    selectedFilters,
  } = state;
  const { entities, totalPages } = await structuredFetch<PcbTestEntity>(
    axios,
    auth,
    'pcb-tests',
    resultsPerPage,
    currentPage,
    sortColumn,
    sortDirection,
    selectedFilters
  );

  return setPcbTests({ entities, totalPages });
};

export const {
  setPcbTests,
  sortPcbTests,
  setSearchFilter,
  setDeviceTypesFilter,
  setDateFilter,
  setStatusFilter,
  setCurrentPage,
  resetPage,
} = pcbTestSlice.actions;

export default pcbTestSlice.reducer;
